// React Query hooks for /staff/teams/* + /staff/departments/* (feature F-A).
// Teams and departments are sibling "Org admin" concerns — one hooks module.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Department,
  DepartmentCreate,
  DepartmentUpdate,
  createDepartment,
  deleteDepartment,
  listDepartments,
  updateDepartment,
} from "../departments";
import {
  MessageResponse,
  Team,
  TeamCreate,
  TeamUpdate,
  createTeam,
  deleteTeam,
  listTeams,
  updateTeam,
} from "../teams";

export const orgKeys = {
  teams: ["teams"] as const,
  departments: ["departments"] as const,
};

function invalidateOrg(qc: ReturnType<typeof useQueryClient>): void {
  // Teams reference departments; a department change can affect team rows.
  qc.invalidateQueries({ queryKey: orgKeys.teams });
  qc.invalidateQueries({ queryKey: orgKeys.departments });
}

// ── Teams ──────────────────────────────────────────────────────────────

export function useTeams() {
  return useQuery<Team[]>({
    queryKey: orgKeys.teams,
    queryFn: () => listTeams(),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation<Team, Error, TeamCreate>({
    mutationFn: createTeam,
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation<Team, Error, { teamId: string; payload: TeamUpdate }>({
    mutationFn: ({ teamId, payload }) => updateTeam(teamId, payload),
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation<MessageResponse, Error, string>({
    mutationFn: deleteTeam,
    onSuccess: () => invalidateOrg(qc),
  });
}

// ── Departments ────────────────────────────────────────────────────────

export function useDepartments() {
  return useQuery<Department[]>({
    queryKey: orgKeys.departments,
    queryFn: () => listDepartments(),
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation<Department, Error, DepartmentCreate>({
    mutationFn: createDepartment,
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation<
    Department,
    Error,
    { departmentId: string; payload: DepartmentUpdate }
  >({
    mutationFn: ({ departmentId, payload }) =>
      updateDepartment(departmentId, payload),
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation<MessageResponse, Error, string>({
    mutationFn: deleteDepartment,
    onSuccess: () => invalidateOrg(qc),
  });
}
